// ============================================================
// DocumentSettingBase
// ------------------------------------------------------------
// คลาสแม่ของหน้า "ตั้งค่าเอกสาร" ทั้ง 5 หน้า
//   จดหมายแจ้งเตือน / ใบเสร็จรับเงิน / ใบเสร็จรายรับอื่นๆ / ใบแจ้งหนี้ตามห้อง / ใบแจ้งหนี้อื่นๆ
//
// ทั้ง 5 หน้าทำงานเหมือนกันทุกอย่าง ต่างกันแค่หน้าตาตัวอย่างเอกสาร (preview)
//   - โหลด/บันทึกที่ API ตัวเดียวกัน ต่างกันแค่ key ในก้อนข้อมูล (letter, receipt, ...)
//   - แผงด้านขวาเป็นสวิตช์เปิด/ปิดแต่ละส่วนของเอกสาร
//   - บางหัวข้อกดดินสอเพื่อแก้ข้อความได้ (popup แก้ไข)
//
// วิธีใช้ — ให้หน้าจอ extends คลาสนี้ แล้วกำหนด
//   documentKey : key ในก้อนข้อมูล เช่น "letter"
//   redirectTo  : path ที่จะไปหลังบันทึกสำเร็จ
//   state       : ต้องเขียนแบบนี้เพื่อให้ได้ค่าเริ่มต้นของคลาสแม่ครบ
//                   state = { ...DocumentSettingBase.INITIAL_STATE, setting: DEFAULT_SETTING };
//                 (class field ของคลาสลูกจะทับ state ของคลาสแม่ทั้งก้อน จึงต้อง spread เอง)
// แล้วเรียกใช้ this.dNone() / this.renderSwitch() / this.renderPanel() / this.renderTextModal() ใน render
// ============================================================

import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import Switch from "react-switch";

import { GET, PUT } from "../core/api.js";
import { tokens, getStorage } from "../core/storage.js";
import { success_url, loading } from "../core/alert.js";
import { ICON } from "./icons.js";

export default class DocumentSettingBase extends Component {
  // key ในก้อนข้อมูล + path หลังบันทึก — คลาสลูกต้องกำหนดทับ
  documentKey = "";
  redirectTo = "/";

  // ค่าเริ่มต้นที่คลาสลูกต้อง spread ลงใน state ของตัวเอง
  static INITIAL_STATE = {
    modal_text: false, // popup แก้ข้อความ
    text: "", // ข้อความที่กำลังแก้
    type: "", // key ของหัวข้อที่กำลังแก้
    document_data: null, // ก้อนข้อมูลตั้งค่าเอกสารทั้งหมดของโครงการ
    setting: {}, // ตั้งค่าเฉพาะเอกสารหน้านี้ (คลาสลูกใส่ค่าเริ่มต้นทับ)
  };

  state = { ...DocumentSettingBase.INITIAL_STATE };

  async componentDidMount() {
    loading(false);
    await this.Get();
  }

  // โหลดตั้งค่าเอกสารของโครงการ — ยังไม่เคยตั้งค่าให้ใช้ค่าเริ่มต้นใน state
  Get = async () => {
    loading(true);
    const result = await GET(tokens, `v1/TH/document-manage/detail/custom-document/${getStorage("project_id")}`, null);
    if (result?.status) {
      this.setState({ document_data: result.data.data, setting: this.mergeSetting(this.state.setting, result.data.data[this.documentKey]) });
    }
    loading(false);
  };

  // รวมค่าที่บันทึกไว้ใน DB เข้ากับค่าเริ่มต้นของหน้า
  // NOTE เดิมใช้ค่าจาก DB ทั้งก้อนแทนค่าเริ่มต้น — ถ้าโครงการบันทึกไว้ตั้งแต่เวอร์ชันที่ยังไม่มีบางหัวข้อ
  //      (หัวข้อที่เพิ่มเข้ามาทีหลัง) หน้าจะอ่าน setting[key].status ของคีย์ที่ไม่มี → TypeError จอขาว
  //      ตอนนี้คีย์ที่ขาดใช้ค่าเริ่มต้น ส่วนคีย์ที่มีอยู่แล้วใช้ค่าจาก DB ตามเดิม (ไม่เปลี่ยนค่าที่ตั้งไว้)
  mergeSetting = (defaults, saved) => {
    if (!saved || typeof saved !== "object") return defaults;
    const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
    const merged = { ...saved };
    Object.entries(defaults).forEach(([key, def]) => {
      if (merged[key] === undefined || merged[key] === null) {
        merged[key] = isObj(def) ? { ...def } : def;
      } else if (isObj(def) && isObj(merged[key])) {
        merged[key] = { ...def, ...merged[key] };
      }
    });
    return merged;
  };

  // บันทึก — ส่งก้อนเดิมทั้งหมดกลับไป แล้วทับเฉพาะ key ของเอกสารหน้านี้
  Update = async () => {
    loading(true);
    const result = await PUT(tokens, "v1/TH/document-manage/update/custom-document", { ...this.state.document_data, [this.documentKey]: this.state.setting });
    if (result?.status) success_url(this.redirectTo);
    loading(false);
  };

  // เปิด/ปิดการแสดงส่วนหนึ่งของเอกสาร
  UpdateStatus = (key) => this.setState((prev) => ({ setting: { ...prev.setting, [key]: { ...prev.setting[key], status: !prev.setting[key].status } } }));

  // คลาสสำหรับซ่อนส่วนที่ปิดสวิตช์ไว้ (ใช้ต่อท้าย className ใน preview)
  dNone = (key) => (this.state.setting[key]?.status ? "" : " d-none");

  // 1 บรรทัดในแผงตั้งค่า — ป้ายชื่อ + (ปุ่มดินสอ) + สวิตช์
  //   hasEdit = true จะมีปุ่มดินสอให้แก้ข้อความของหัวข้อนั้น
  renderSwitch = (label, key, hasEdit = false) => {
    return (
      <div className="d-flex flex-wrap mb-1" key={key}>
        <div className={`wpx-140${hasEdit ? " d-flex" : ""}`}>
          {label}
          {hasEdit && (
            <label
              className="wpx-20 hpx-20 bg-warning-light rounded-1 d-flex align-items-center justify-content-center icon text-warning pointer mx-1"
              onClick={() => this.setState({ modal_text: true, text: this.state.setting[key].title, type: key })}
            >
              {ICON.edit}
            </label>
          )}
        </div>
        <Switch height={20} width={40} checked={this.state.setting[key].status} onChange={() => this.UpdateStatus(key)} />
      </div>
    );
  };

  // แผงตั้งค่า 1 กลุ่ม — { title, items: [[label, key, hasEdit], ...] }
  renderPanel = (group) => (
    <div className="py-3 px-4" key={group.title}>
      <h5 className="fw-bold">{group.title}</h5>
      {group.items.map(([label, key, hasEdit]) => this.renderSwitch(label, key, hasEdit))}
    </div>
  );

  // popup แก้ข้อความของหัวข้อที่กดดินสอ
  renderTextModal = () => (
    <Modal show={this.state.modal_text} onHide={() => this.setState({ modal_text: false })}>
      <Modal.Header>
        <Modal.Title>
          <b>แก้ไข</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="w-100 d-flex align-items-start mb-3">
          <label className="wpx-50">หัวข้อ :</label>
          <textarea className="form-control border-0 bg-contain validate" rows={5} placeholder="กรอกข้อมูล" onChange={(e) => this.setState({ text: e.target.value })} value={this.state.text} />
        </div>
        <div className="w-100 d-flex">
          <div className="w-50 p-1">
            <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_text: false })}>
              ยกเลิก
            </button>
          </div>
          <div className="w-50 p-1">
            <button className="btn btn-primary w-100" onClick={this.saveText}>
              บันทึก
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );

  // บันทึกข้อความที่แก้ใน popup ลง setting
  saveText = () => {
    const setting = { ...this.state.setting };
    setting[this.state.type].title = this.state.text;
    this.setState({ setting, modal_text: false });
  };
}
